import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicMobileNav } from "@/components/PublicMobileNav";
import {
  ArrowRight, ClipboardList, Columns3, Search, Sparkles,
  Mail, ShieldCheck, Store, Zap,
} from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { PublicFooter } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { VendorLogo, Badges } from "@/pages/Marketplace";
import { VisitorRfqDialog } from "@/components/VisitorRfqDialog";

const pub = axios.create({ baseURL: API });

const SHOWCASE_POSTER = "/showcase-poster.jpg";

const HERO = {
  contractor: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  vendor: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const contractorFeatures = [
  { icon: Search, title: "Find vetted vendors", desc: "Search suppliers and subcontractors by trade, with live licence status on every profile." },
  { icon: ClipboardList, title: "One RFQ, many vendors", desc: "Type your scope or upload your BOQ in Excel, then invite as many vendors as you like." },
  { icon: Mail, title: "Send your way", desc: "Invite Matiqs vendors instantly, or email vendors you already work with — both land in the same comparison." },
  { icon: Columns3, title: "Compare side by side", desc: "Totals up top, line-by-line detail underneath, cheapest rate highlighted automatically." },
];

const vendorFeatures = [
  { icon: Zap, title: "AI BOQ matching", desc: "Contractor BOQs are matched line by line — headers, sub-totals and tabs stay exactly as sent." },
  { icon: Store, title: "Be discoverable", desc: "A public profile with your trades, projects and verified documents, in front of live contractors." },
  { icon: ShieldCheck, title: "Documents that verify themselves", desc: "Upload your trade licence and Matiqs reads the expiry date and tracks renewals for you." },
];

const Stat = ({ value, label, testid }) => (
  <div data-testid={testid} className="px-6 py-5">
    <div className="font-display text-3xl font-extrabold text-slate-900 dark:text-white tabular-nums">{value}</div>
    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold mt-1">{label}</div>
  </div>
);

const HeroPanel = ({ side, eyebrow, headline, sub, cta, testid, image, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: side === "left" ? 0.05 : 0.15 }}
    data-testid={testid}
    className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900 p-8 md:p-10 flex flex-col justify-between min-h-[420px] group"
  >
    <div
      className="absolute inset-0 opacity-[0.22] group-hover:opacity-30 transition-opacity duration-500"
      style={{ backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center" }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-900/50" />
    <div className="relative">
      <span className="inline-block px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-200">
        {eyebrow}
      </span>
      <h2 className="font-display text-3xl md:text-[2.6rem] font-extrabold text-white leading-[1.1] tracking-tight mt-6">
        {headline}
      </h2>
      <p className="text-slate-300 text-sm md:text-base mt-4 max-w-md leading-relaxed">{sub}</p>
    </div>
    <div className="relative mt-8">
      <Button
        onClick={onClick}
        className="h-12 px-7 text-sm font-bold gap-2 bg-white text-slate-900 hover:bg-white/90 active:scale-[0.98] transition-all"
      >
        {cta} <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  </motion.div>
);

const FeatureColumn = ({ label, tone, items, cta, onCta, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-2xl p-7">
    <div className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] ${tone}`}>
      {label}
    </div>
    <div className="mt-7 space-y-6">
      {items.map((f) => (
        <div key={f.title} className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-secondary border border-border flex items-center justify-center shrink-0">
            <f.icon className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-100">{f.title}</div>
            <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.desc}</div>
          </div>
        </div>
      ))}
    </div>
    <button onClick={onCta} className="mt-7 text-sm font-bold text-primary inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
      {cta} <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [visitorVendor, setVisitorVendor] = useState(null);

  useEffect(() => {
    pub.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    pub.get("/public/vendors").then((r) => setVendors((r.data.vendors || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="home-brand" className="shrink-0">
            <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <Link data-testid="home-nav-marketplace" to="/marketplace" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Marketplace</Link>
            <Link data-testid="home-nav-einvoicing" to="/einvoicing" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Matiqs Qonnect</Link>
            <Link data-testid="home-nav-pricing" to="/pricing" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
            <Link data-testid="home-nav-how" to="/how-it-works" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">How it works</Link>
            <Link data-testid="home-nav-faq" to="/faq" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid="home-signin-button" variant="outline" onClick={() => navigate("/login")} className="hidden md:flex h-10 font-semibold" title="One sign-in for contractors and vendors">Sign in</Button>
            <Button data-testid="home-signup-button" onClick={() => navigate("/login?mode=register")} className="hidden md:flex h-10 font-semibold">Get started</Button>
            <PublicMobileNav />
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-fade pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 pt-14 pb-12 md:pt-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-soft text-brand text-[11px] font-bold uppercase tracking-[0.16em] border border-brand/20">
                <Sparkles className="w-3 h-3" /> Powerful free plan · Unbeatable low plans
              </span>
              <Link to="/einvoicing" data-testid="home-einvoice-badge" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold uppercase tracking-[0.16em] border border-emerald-300 dark:border-emerald-800 hover:bg-emerald-100 transition-colors">
                <ShieldCheck className="w-3 h-3" /> UAE e-invoicing ready
              </Link>
            </div>
            <p data-testid="home-einvoice-subtext" className="text-xs md:text-sm font-semibold text-emerald-700 dark:text-emerald-300 mt-3">
              In compliance with the UAE FTA e-invoicing mandate · Peppol PINT-AE (UBL 2.1) format ready · 5% VAT tax invoices &amp; credit notes
            </p>
            <h1 data-testid="home-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-slate-900 dark:text-white mt-6">
              Smart Procurement to E-Invoicing.<br className="hidden sm:block" />
              <span className="text-primary"> All in One Platform.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-5">
              AI-powered procurement, effortless quotations, and seamless UAE e-invoicing compliance—built to streamline your entire construction workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-14">
            <HeroPanel
              side="left"
              testid="hero-buyer-panel"
              eyebrow="For contractors"
              headline={<>Receive and Compare Quotations Instantly. Save Time and Cost Consistently.</>}
              sub="Send one RFQ to every vendor you trust, then read all their prices in a single grid instead of ten inboxes."
              cta="Get started"
              image={HERO.contractor}
              onClick={() => navigate("/login?type=buyer&mode=register")}
            />
            <HeroPanel
              side="right"
              testid="hero-vendor-panel"
              eyebrow="For suppliers & subcontractors"
              headline={<>Faster Quotes. More Opportunities.</>}
              sub="Import your rate list once and let Matiqs price incoming BOQs automatically, while contractors find you in the marketplace."
              cta="Get started"
              image={HERO.vendor}
              onClick={() => navigate("/for-vendors")}
            />
          </div>
        </div>
      </section>

      {stats && (
        <section className="border-y border-border bg-card">
          <div data-testid="trust-signals" className="max-w-6xl mx-auto px-5 flex flex-wrap divide-x divide-border">
            <Stat testid="stat-vendors" value={stats.vendors} label="Vendors listed" />
            <Stat testid="stat-trades" value={stats.trades} label="Trades covered" />
            <Stat testid="stat-items" value={stats.catalogue_items.toLocaleString()} label="Priced line items" />
            <Stat testid="stat-quotes" value={stats.quotes} label="Quotations built" />
          </div>
        </section>
      )}

      <section data-testid="automation-showcase" className="max-w-6xl mx-auto px-5 pt-16">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-card text-brand text-[11px] font-bold uppercase tracking-[0.16em]">
            Showcase Your Automation Capabilities
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-4">
            Quote Smarter, Win Faster.
          </h2>
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-border bg-slate-950 shadow-xl ring-1 ring-black/5">
          <video
            data-testid="automation-showcase-video"
            className="w-full h-auto block"
            controls
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={SHOWCASE_POSTER}
          >
            <source
              src="https://customer-assets-jt897jd0.emergentagent.net/job_matiqs-auto/artifacts/q2ct7czq_lv_0_20260929180001.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        </div>
      </section>


      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Vendor marketplace</div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">
              Who is already quoting automatically on Matiqs
            </h2>
          </div>
          <Link data-testid="browse-all-vendors" to="/marketplace" className="text-sm font-bold text-primary inline-flex items-center gap-1.5 hover:gap-2.5 transition-all shrink-0 pb-1">
            Browse all vendors <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div data-testid="vendor-showcase" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(i * 0.05, 0.3) }}
              className="bg-card border border-border rounded-2xl p-5 hover:border-primary/40 hover:shadow-lg shadow-slate-900/5 transition-all duration-200"
            >
              <button
                data-testid={`showcase-vendor-${i}`}
                onClick={() => navigate(`/vendor/${v.id}`)}
                className="w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <VendorLogo vendor={v} size={44} />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{v.company_name}</div>
                    <div className="text-xs text-muted-foreground">{[v.city, v.country].filter(Boolean).join(", ") || v.vendor_type}</div>
                  </div>
                </div>
                {v.business_types?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-3">{v.business_types.slice(0, 3).join(" · ")}</div>
                )}
                <div className="mt-3"><Badges v={v} /></div>
              </button>
              <Button data-testid={`showcase-send-rfq-${i}`} onClick={() => setVisitorVendor(v)}
                      className="w-full mt-4 h-10 gap-2 font-semibold bg-brand text-brand-foreground hover:bg-brand/90">
                <Mail className="w-4 h-4" /> Send RFQ
              </Button>
            </motion.div>
          ))}
        </div>
      </section>

      {visitorVendor && <VisitorRfqDialog vendor={visitorVendor} onClose={() => setVisitorVendor(null)} />}

      <section data-testid="qompare-section" className="border-t border-border">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="rounded-3xl border border-border bg-gradient-to-br from-brand-soft/60 to-secondary/40 p-8 sm:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-card text-brand text-xs font-bold uppercase tracking-wider">
                <Columns3 className="w-3.5 h-3.5" /> Matiqs Qompare
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-5">
                Already have quotes in your inbox?
              </h2>
              <p className="text-base text-muted-foreground mt-3 max-w-2xl leading-relaxed">
                Got quotations from your usual vendors by email? Use <strong className="text-foreground">Matiqs Qompare</strong> for
                free — drop in each PDF or Excel, and see every price side by side, line for line, cheapest highlighted.
                No RFQ to send, no waiting.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-muted-foreground">
                {["Upload PDF or Excel quotes", "Line-by-line comparison", "Always free"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-brand" />{t}</span>
                ))}
              </div>
            </div>
            <div className="shrink-0">
              <Button data-testid="home-qompare-cta" onClick={() => navigate("/login?type=buyer&mode=register")}
                      className="h-12 px-7 text-base font-semibold gap-2">
                Compare quotes free <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white text-center">
            Built for both sides of the table
          </h2>
          <div className="grid md:grid-cols-2 gap-5 mt-10">
            <FeatureColumn
              testid="features-contractor"
              label="Contractors"
              tone="bg-brand-soft text-brand border border-brand/25"
              items={contractorFeatures}
              cta="Create a contractor account"
              onCta={() => navigate("/login?type=buyer&mode=register")}
            />
            <FeatureColumn
              testid="features-vendor"
              label="Suppliers & subcontractors"
              tone="bg-primary/10 text-primary border border-primary/20"
              items={vendorFeatures}
              cta="Create a vendor account"
              onCta={() => navigate("/for-vendors")}
            />
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="max-w-6xl mx-auto px-5 py-16 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Ready to stop chasing quotations?
          </h2>
          <p className="text-muted-foreground mt-3">
            <span className="font-semibold text-foreground">Powerful free plan. Unbeatable low plans.</span>{" "}
            Set up takes under two minutes on either side, no card needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button data-testid="footer-cta-buyer" onClick={() => navigate("/login?type=buyer&mode=register")} className="h-12 px-7 font-bold gap-2">
              I'm a Contractor <ArrowRight className="w-4 h-4" />
            </Button>
            <Button data-testid="footer-cta-vendor" variant="outline" onClick={() => navigate("/for-vendors")} className="h-12 px-7 font-bold gap-2">
              I'm a Vendor <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <Link data-testid="footer-cta-pricing" to="/pricing" className="inline-block text-sm font-bold text-primary hover:underline mt-6">
            See what every plan includes
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
